import { useMemo } from 'react';
import { isInstanceHidden } from '@/features/events/lib/eventVisibility.lib';
import { useSourceEvents } from '@/features/sources/hooks/useSourceEvents';
import {
  eventMatchesFeed,
  isFeedAccessible,
} from '@/features/sources/lib/feed.lib';
import { expandSourceEventInstances } from '@/features/sources/lib/sourceEventInstance.lib';
import type { ExperienceFeed } from '@/types/gql.generated';
import { Box, Flex, Spinner } from '@/ui';
import { FeedDetailHeader } from './FeedDetailHeader';
import { SourceDefaults } from './SourceDefaults';
import { SourceEventList } from './SourceEventList';
import { useShowHiddenSourceEvents } from './useShowHiddenSourceEvents';

// Per-feed data on experiences: only id/title/originalTitle/color is currently available.
// See each sub-component for granular TODO(backend) markers on each missing field/mutation.

type Props = {
  feed: ExperienceFeed;
};

export const FeedDetail = ({ feed }: Props) => {
  // Scoped server-side to this source, today onward — see useSourceEvents for why
  // the experience-wide event list can't be used here. Only the selected source
  // fetches: FullScreenPanel instantiates the active section's content() alone.
  // A revoked source returns zero events by design, so the defaults and the
  // event list below would render as an empty shell under the header's banner.
  // The banner already says what is going on; leave it as the whole story, and
  // do not ask the server for a list known to be empty.
  const isAccessible = isFeedAccessible(feed);
  const { events, isLoading, rangeStart, rangeEnd } = useSourceEvents(feed, {
    enabled: isAccessible,
  });

  // The `sources` facet already restricts the response to this feed, so this is
  // a cheap guard against an event resolving to a different origin when it is
  // reachable through more than one collection.
  const feedEvents = useMemo(
    () => events.filter((e) => eventMatchesFeed(e, feed)),
    [events, feed]
  );

  // A recurring series arrives as one row dated to its (often long past) start,
  // so the list works in occurrences: each one still to come inside the window
  // the query covers. Hidden occurrences are resolved inside the expansion so
  // they are dropped before the row cap rather than after it — see
  // expandSourceEventInstances.
  const { isShowingHidden } = useShowHiddenSourceEvents();
  const { instances, isTruncated, hasHidden } = useMemo(
    () =>
      expandSourceEventInstances(
        feedEvents,
        rangeStart,
        rangeEnd,
        isShowingHidden
      ),
    [feedEvents, rangeStart, rangeEnd, isShowingHidden]
  );

  return (
    // Symmetric horizontal padding reserves a right-margin gutter for the
    // hover-revealed per-row hide button (which floats ~44px past the row's
    // right edge), keeping it within the panel's scroll area without
    // horizontal overflow; the matching left padding keeps the content balanced.
    <Box maxW="928px" px={6}>
      <FeedDetailHeader feed={feed} hasHidden={hasHidden} />
      {isAccessible &&
        (isLoading ? (
          <Flex justify="center" pt={10}>
            <Spinner color="magicPurple.500" />
          </Flex>
        ) : (
          <>
            {/* The soonest upcoming occurrence stands in for the source in the
              preview card, rather than whichever event the server listed first.
              Hidden occurrences are passed over, the way the Schedule's source
              preview picks its stand-in. */}
            <SourceDefaults
              feed={feed}
              previewEvent={
                instances.find(
                  ({ event, startDate }) => !isInstanceHidden(event, startDate)
                )?.event ?? null
              }
            />
            <SourceEventList
              feed={feed}
              hasHidden={hasHidden}
              instances={instances}
              isTruncated={isTruncated}
              rangeEnd={rangeEnd}
              rangeStart={rangeStart}
            />
          </>
        ))}
    </Box>
  );
};
